import { FlexRow, FlexRowBetween, NumericStepper, TitleText } from 'components'
import React, { useState, useEffect } from 'react'
import { Modal, Form, Checkbox, Button } from 'semantic-ui-react'
import { DotsSixVertical, Plus, Trash, X } from 'phosphor-react'
import {
    AppPrimaryButton,
    FormColorFormField,
    FormTextAreaFormField,
    FormTextFormField,
    FormToggleFormField,
    SubmitCancelButtons,
} from 'elements'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import {
    AcceptFileType,
    CommonUtility,
    FormErrorText,
    InspectionService,
    TemplateQuestionResponseType,
    TemplateService,
} from 'utility'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { FileUploadPicker } from 'components/FileUploadPicker'
import { CustomePreview } from 'page-components/inspection/Elements'
import { read, utils } from 'xlsx'

const ColorBox = styled.div`
    width: 40px;
    height: 40px;
    color: ${({ color }) => color};
    background: ${({ color }) => color};
    border-radius: 50%;
`
const AddResponseButton = styled.div`
    display: flex;
    align-items: center;
    border: solId 2px darkgrey;
    border-style: dashed;
    border-radius: 5px;
    padding: 10px;
    justify-content: center;
`

const BorderResponse = styled.div`
    border: solid 2px darkgrey;
    border-style: dashed;
    border-radius: 5px;
    padding: 10px;
    margin-block: 10px;
    padding: 0.5em;
    margin: 0.5em 0;
    border-radius: 5px;
    background-color: ${({ theme, isDragging }) =>
        isDragging ? theme.colors.hover : theme.colors.white};
    color: ${({ theme, isDragging }) =>
        isDragging ? theme.colors.white : theme.colors.black};
    top: auto !important;
    left: auto !important;
    display: flex;
    justify-content: space-between;
    .w-100 {
        width: 100%;
    }
    .trash {
        svg {
            color: ${({ theme }) => theme.colors.danger};
        }
    }
    .suggestions-container {
        position: relative;
        width: 100%;
    }

    .suggestions-list {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-radius: 4px;
        max-height: 200px;
        overflow-y: auto;
        z-index: 1000;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .suggestion-item {
        padding: 8px 12px;
        cursor: pointer;
        &:hover {
            background-color: #f5f5f5;
        }
    }
`

const MediaContainer = styled.div`
    .media-item {
        width: 100px !important;
        height: 100px !important;
    }
`

export const ResponseTypeCheckBoxes = ({ list, onChange, value }) => {
    return (
        <>
            {list.map(ele => (
                <FlexRow
                    onClick={() =>
                        onChange(value === ele.Id ? undefined : ele.Id)
                    }
                    className="align-items-center"
                    key={ele.Id}
                >
                    <Checkbox checked={value === ele.Id} />
                    <span className="ms-3">{ele.ControlType}</span>
                </FlexRow>
            ))}
        </>
    )
}
const AdditionalInfoSchema = yup.object().shape({
    AdditionalInfo: yup
        .string()
        .trim(FormErrorText.SpacesNotAllowed)
        .required('Additional Info is required')
        .nullable(),
    IsShowFullAddtionalInfo: yup.boolean(),
    IsHint: yup.boolean(),
    AddtionalInfoImage: yup.array().max(1),
})

export const AddAdditionalInfoModal = ({ open, onClose, data }) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(AdditionalInfoSchema),
        defaultValues: {
            AdditionalInfo: '',
            IsShowFullAddtionalInfo: false,
            IsHint: false,
        },
    })

    const [files, setFiles] = useState([])
    const [loading, setLoading] = useState(false)

    // Reset form and files when data changes
    useEffect(() => {
        if (data) {
            reset({
                AdditionalInfo: data.AdditionalInfo || '',
                IsShowFullAddtionalInfo: data.IsShowFullAddtionalInfo || false,
                IsHint: data.IsHint || false,
            })

            if (data.AddtionalInfoImage) {
                setFiles([
                    {
                        id: new Date().getTime().toString(),
                        url: data.AddtionalInfoImage,
                        name: CommonUtility.getFileNameFromUrl(
                            data.AddtionalInfoImage,
                        ),
                        local: false,
                        SignedURL: data.AddtionalInfoImage,
                    },
                ])
            } else {
                setFiles([])
            }
        } else {
            // Reset to defaults when creating new
            reset({
                AdditionalInfo: '',
                IsShowFullAddtionalInfo: false,
                IsHint: false,
            })
            setFiles([])
        }
    }, [data, reset, open]) // Added open to dependency array to reset when modal opens

    const onFileUpload = async uploadedFiles => {
        try {
            const uploadPromises = uploadedFiles.map(async file => {
                if (file.local) {
                    // Handle both regular file uploads and captured images
                    const fileToUpload = file.file?.file || file.file

                    // Upload the file
                    const uploadResponse = await InspectionService.uploadMedia({
                        media: fileToUpload,
                        type: fileToUpload.type,
                    })

                    // Save media record
                    const mediaPayload = [
                        {
                            MediaId: 0,
                            FAQId: '',
                            Order: 1,
                            FolderName: '1',
                            Comment: uploadResponse?.Comment || '',
                            Caption: uploadResponse?.Caption || '',
                            FileType:
                                uploadResponse?.FileType ||
                                fileToUpload.type ||
                                file.file?.type ||
                                '',
                            SignedURL:
                                uploadResponse?.ImagePathUrl ||
                                uploadResponse?.FileURL,
                            FileURL:
                                uploadResponse?.ImagePathUrl ||
                                uploadResponse?.FileURL,
                            FileKey:
                                uploadResponse?.CloudImageId ||
                                uploadResponse?.FileKey,
                        },
                    ]

                    const savedMedia = await InspectionService.saveMedia(
                        mediaPayload,
                    )

                    // Update the files state
                    const savedFile = {
                        ...savedMedia[0],
                        local: false,
                        id: savedMedia[0].MediaId,
                        name:
                            uploadResponse.FileKey ||
                            fileToUpload.name ||
                            'File',
                        url:
                            uploadResponse.FileURL ||
                            uploadResponse.ImagePathUrl,
                        preview:
                            uploadResponse.FileURL ||
                            uploadResponse.ImagePathUrl,
                        type: fileToUpload.type,
                        FileURL:
                            uploadResponse.FileURL ||
                            uploadResponse.ImagePathUrl,
                        FileKey: uploadResponse.FileKey,
                        SignedURL:
                            uploadResponse.ImagePathUrl ||
                            uploadResponse.FileURL,
                    }

                    setFiles([savedFile])
                    return savedFile
                }
                return file
            })

            await Promise.all(uploadPromises)
        } catch (error) {
            console.error('File upload error:', error)
        }
    }

    const onRemoveFile = async file => {
        try {
            if (!file.local && file.MediaId) {
                await InspectionService.deleteMedia(file.MediaId)
            }
            setFiles([])
        } catch (error) {
            console.error('Error removing file:', error)
        }
    }

    const saveComment = (item, data) => {
        if (data.Comment || data.Caption) {
            const updatedFiles = files.map(f => {
                if (f.id === item.id) {
                    return { ...f, ...data }
                }
                return f
            })
            setFiles(updatedFiles)
        }
    }

    const submit = async formData => {
        setLoading(true)
        try {
            const submitData = {
                AdditionalInfo: formData.AdditionalInfo,
                IsShowFullAddtionalInfo: formData.IsShowFullAddtionalInfo,
                IsHint: formData.IsHint,
                AddtionalInfoImage: files[0]?.SignedURL || null,
            }
            console.log('Submitting data:', submitData)
            onClose(submitData)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal open={open} onClose={() => onClose()} className="model">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>
                        {data &&
                        (data.AdditionalInfo ||
                            data.AddtionalInfoImage ||
                            data.IsShowFullAddtionalInfo ||
                            data.IsHint)
                            ? 'Edit'
                            : 'Add'}{' '}
                        Additional Info
                    </TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <ResponseContainer>
                <Form noValidate>
                    <Form.Group widths="equal">
                        <FormTextAreaFormField
                            control={control}
                            label="Additional Info"
                            name="AdditionalInfo"
                            placeholder="Please write additional info here"
                            errors={errors?.AdditionalInfo}
                            widths={16}
                        />
                    </Form.Group>

                    <Form.Field>
                        <MediaContainer>
                            <FileUploadPicker
                                maxFiles={1}
                                label="Additional Info Image"
                                maxFileSize={500}
                                accept={AcceptFileType.all}
                                addedNewFiles={onFileUpload}
                                onRemove={onRemoveFile}
                                files={files}
                                remove
                                customePreview
                                previewItem={
                                    <CustomePreview
                                        saveComment={saveComment}
                                        className="media-item"
                                    />
                                }
                                previewItemClassName="media-item"
                                tagPreview={false}
                            />
                        </MediaContainer>
                    </Form.Field>

                    <Form.Group>
                        <FormToggleFormField
                            control={control}
                            label="Show Full Additional Info"
                            name="IsShowFullAddtionalInfo"
                            placeholder="IsShowFullAddtionalInfo"
                            errors={errors?.IsShowFullAddtionalInfo}
                        />
                        <FormToggleFormField
                            control={control}
                            label="Is Hint"
                            name="IsHint"
                            placeholder="IsHint"
                            errors={errors?.IsHint}
                        />
                    </Form.Group>

                    <SubmitCancelButtons
                        onSubmit={handleSubmit(submit)}
                        onCancel={() => onClose()}
                        loading={loading}
                    />
                </Form>
            </ResponseContainer>
        </Modal>
    )
}

const ResponseItem = ({
    item,
    remove,
    onchange,
    setScoreFromQuestion,
    handleDisplayTextChange,
    suggestions,
    handleSuggestionClick,
    control,
    errors,
    list,
    onOpenHintModal,
}) => {
    return (
        <BorderResponse className="mt-1" isDragging={false}>
            <Form.Group className="w-100">
                <div>
                    <DotsSixVertical size={32} />
                </div>
                <Form.Field width={16}>
                    <div className="suggestions-container">
                        <FormTextFormField
                            control={control}
                            name={`DisplayText-${item.Id}`}
                            label="DisplayText"
                            value={item.DisplayText}
                            errors={errors?.DisplayText}
                            onChange={value => {
                                handleDisplayTextChange(value, item.Id)
                            }}
                        />
                        {suggestions.length > 0 && (
                            <div className="suggestions-list">
                                {suggestions.map((suggestion, i) => (
                                    <div
                                        key={i}
                                        className="suggestion-item"
                                        onClick={() => {
                                            handleSuggestionClick(
                                                suggestion,
                                                item.Id,
                                            )
                                        }}
                                    >
                                        {suggestion}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <FlexRow>
                        <FormTextFormField
                            control={control}
                            type="number"
                            name="Score"
                            label="Score"
                            placeholder="0"
                            value={item.Score}
                            errors={errors?.Score}
                            disabled={!!item.AllowManualScore}
                            onChange={value => {
                                onchange('Score', value, item.Id)
                            }}
                        />
                        <FormColorFormField
                            control={control}
                            name="BackColorCode"
                            label="Color"
                            height="38px"
                            value={item.BackColorCode}
                            onChange={value => {
                                onchange('BackColorCode', value, item.Id)
                            }}
                        />
                        <div className="flex justify-center items-center w-full">
                            <AppPrimaryButton
                                type="button"
                                icon
                                size="small"
                                onClick={() =>
                                    onOpenHintModal(list.indexOf(item))
                                }
                                color={
                                    item.Hints?.length > 0 ? 'yellow' : 'teal'
                                }
                                title={
                                    item.Hints?.length > 0
                                        ? 'Edit Hint'
                                        : 'Add Hint'
                                }
                            >
                                {item.Hints?.length > 0
                                    ? 'Edit Hint'
                                    : 'Add Hint'}
                            </AppPrimaryButton>
                        </div>
                        {list.length > 1 && (
                            <Form.Field
                                className="d-flex align-items-center trash"
                                width={2}
                            >
                                <Trash
                                    size={32}
                                    onClick={() => remove(item.Id)}
                                />
                            </Form.Field>
                        )}
                    </FlexRow>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            marginTop: 8,
                        }}
                    >
                        <div className="flex items-center">
                            <Checkbox
                                checked={item.IsScoreOnThisResponse}
                                onChange={() => {
                                    const isChecked =
                                        !item.IsScoreOnThisResponse
                                    item.IsScoreOnThisResponse = isChecked
                                    if (isChecked) {
                                        setScoreFromQuestion(item.Id, true)
                                    } else {
                                        setScoreFromQuestion(item.Id, false)
                                    }
                                    onchange(
                                        'IsScoreOnThisResponse',
                                        item.IsScoreOnThisResponse,
                                        item.Id,
                                    )
                                }}
                            />
                            <span className="ms-3">
                                Take Score from Question
                            </span>
                        </div>
                        <div className="flex items-center">
                            <Checkbox
                                checked={item.IsExcludedFromScoring || false}
                                onChange={() => {
                                    const isChecked =
                                        !item.IsExcludedFromScoring
                                    onchange(
                                        'IsExcludedFromScoring',
                                        isChecked,
                                        item.Id,
                                    )
                                }}
                            />
                            <span className="ms-3">
                                Exclude from Score Calculation
                            </span>
                        </div>

                        <div className="flex items-center">
                            <Checkbox
                                checked={!!item.AllowManualScore}
                                onChange={() => {
                                    const isChecked = !item.AllowManualScore
                                    item.AllowManualScore = isChecked

                                    // When manual score is enabled, do not take score from question.
                                    if (isChecked) {
                                        item.IsScoreOnThisResponse = false
                                        item.fetchedScore = undefined
                                        onchange(
                                            'IsScoreOnThisResponse',
                                            false,
                                            item.Id,
                                        )
                                    }

                                    onchange(
                                        'AllowManualScore',
                                        isChecked,
                                        item.Id,
                                    )

                                    if (!isChecked) {
                                        onchange('MaxManualScore', 0, item.Id)
                                    }
                                }}
                            />
                            <span className="ms-3">
                                Allow Manual Score
                            </span>
                        </div>

                        {item.AllowManualScore && (
                            <div style={{ marginTop: 2 }}>
                                <label
                                    style={{
                                        display: 'block',
                                        marginBottom: 6,
                                        fontSize: '0.85rem',
                                        color: '#666',
                                    }}
                                >
                                    Maximum score that can be entered
                                </label>
                                <NumericStepper
                                    min={0}
                                    step={1}
                                    value={item.MaxManualScore ?? 0}
                                    onChange={next =>
                                        onchange(
                                            'MaxManualScore',
                                            next,
                                            item.Id,
                                        )
                                    }
                                />
                            </div>
                        )}
                    </div>
                </Form.Field>
                <Form.Field width={2} className="mt-4">
                    <ColorBox color={item.BackColorCode}>&nbsp;</ColorBox>
                </Form.Field>
            </Form.Group>
        </BorderResponse>
    )
}

const List = ({
    list,
    remove,
    control,
    errors,
    onchange,
    onDragEnd,
    setScoreFromQuestion,
    handleDisplayTextChange,
    suggestions,
    handleSuggestionClick,
    activeField,
    onOpenHintModal,
}) => {
    console.log('Rendering responces in List:', list)
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
                {provided => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                        {list.map((item, index) => (
                            <Draggable
                                key={item.Id}
                                draggableId={item.Id.toString()}
                                index={index}
                            >
                                {provided => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                    >
                                        <ResponseItem
                                            item={item}
                                            remove={remove}
                                            onchange={onchange}
                                            setScoreFromQuestion={
                                                setScoreFromQuestion
                                            }
                                            handleDisplayTextChange={
                                                handleDisplayTextChange
                                            }
                                            suggestions={
                                                activeField === item.Id
                                                    ? suggestions
                                                    : []
                                            }
                                            handleSuggestionClick={
                                                handleSuggestionClick
                                            }
                                            control={control}
                                            errors={errors}
                                            list={list}
                                            onOpenHintModal={onOpenHintModal}
                                        />
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    )
}
const ResponseContainer = styled(Modal.Content)`
    .text-align-end {
        text-align: end;
    }
`

export const ResponseEditorWrap = styled.div`
    .text-align-end {
        text-align: end;
    }
`

export const ResponseHintModal = ({
    open,
    onClose,
    hintHeading,
    onHintHeadingChange,
    hintText,
    onHintTextChange,
    hintHeadingError,
    hintFiles,
    onHintFileUpload,
    onRemoveHintFile,
    hintUploadLoading,
    onSave,
}) => (
    <Modal open={open} onClose={onClose} className="model" size="small">
        <Modal.Header>
            <FlexRowBetween>
                <TitleText>
                    {hintHeading || hintText ? 'Edit Hint' : 'Add Hint'}
                </TitleText>
                <X className="pointer" size={32} onClick={onClose} />
            </FlexRowBetween>
        </Modal.Header>
        <Modal.Content>
            <Form>
                <Form.Field>
                    <label>Heading</label>
                    <input
                        type="text"
                        value={hintHeading}
                        onChange={e => onHintHeadingChange(e.target.value)}
                        placeholder="Enter heading"
                    />
                    {hintHeadingError && (
                        <span style={{ color: 'red' }}>{hintHeadingError}</span>
                    )}
                </Form.Field>
                <Form.Field>
                    <label>Text</label>
                    <textarea
                        value={hintText}
                        onChange={e => onHintTextChange(e.target.value)}
                        placeholder="Enter hint text"
                        rows={4}
                    />
                </Form.Field>
                <FileUploadPicker
                    label="Media"
                    maxFiles={1}
                    files={hintFiles}
                    addedNewFiles={onHintFileUpload}
                    onRemove={onRemoveHintFile}
                    showTakePicture
                />
                <FlexRow className="justify-content-end mt-2">
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        color="blue"
                        onClick={onSave}
                        disabled={
                            hintUploadLoading ||
                            (hintFiles.length &&
                                !(
                                    hintFiles[0]?.FileURL || hintFiles[0]?.url
                                ))
                        }
                    >
                        Save
                    </Button>
                </FlexRow>
            </Form>
        </Modal.Content>
    </Modal>
)
const LIST_BOX_RESPONSE_TYPE = TemplateQuestionResponseType.Multiple

const parseNonNegativeInt = (raw, fallback = 0) => {
    if (raw === '' || raw == null) return fallback
    const digits = String(raw).replace(/\D/g, '')
    if (digits === '') return fallback
    const num = parseInt(digits, 10)
    return Number.isNaN(num) ? fallback : Math.max(0, num)
}

const formatNonNegativeInt = (num, fallback = 0) =>
    String(parseNonNegativeInt(num, fallback))

const CreateResponseModal = ({
    open,
    data,
    onClose,
    templateId,
    multipleResponcesList,
    questionScore,
    templateNumericId,
    isOtherOptionAvailable: isOtherOptionAvailableProp,
    initialMinAnswer = 0,
    initialMaxAnswer = 1,
    onMinMaxChange,
}) => {
    const editMode = !!(data && data.Items && data.Items.length)
    console.log(
        'CreateResponseModal - Received templateId:',
        templateId,
        templateNumericId,
    )

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        // resolver: yupResolver(ResponcesSchema),
    })
    const [responces, setResponces] = useState([])
    const [lastMultipleElement, setLastMultipleElement] = useState({})
    const [groupId, setGroupId] = useState(null)
    const [respType, setrespType] = useState(null)
    const [files, setFiles] = useState([])
    const [fileError, setFileError] = useState(false)
    const [loading, setLoading] = useState()
    const [excelData, setExcelData] = useState([]) // Store Excel data for suggestions
    const [suggestions, setSuggestions] = useState([]) // Store filtered suggestions
    const [activeField, setActiveField] = useState(null) // Track which field is being edited
    const [hintModalOpen, setHintModalOpen] = useState(false)
    const [currentHintIndex, setCurrentHintIndex] = useState(null)
    const [hintHeading, setHintHeading] = useState('')
    const [hintText, setHintText] = useState('')
    const [hintFiles, setHintFiles] = useState([])
    const [hintUploadLoading, setHintUploadLoading] = useState(false)
    const [isOtherOptionAvailable, setIsOtherOptionAvailable] = useState(false)
    const [hintHeadingError, setHintHeadingError] = useState('')
    const [minAnswer, setMinAnswer] = useState(0)
    const [maxAnswer, setMaxAnswer] = useState(1)
    const [minAnswerInput, setMinAnswerInput] = useState('0')
    const [maxAnswerInput, setMaxAnswerInput] = useState('1')

    const isListBox = respType === LIST_BOX_RESPONSE_TYPE

    const notifyMinMaxChange = (min, max) => {
        if (onMinMaxChange) {
            onMinMaxChange({
                MinAnswer: Number(min),
                MaxAnswer: Number(max),
            })
        }
    }

    const commitMinAnswer = (raw, notify = true) => {
        const min = parseNonNegativeInt(raw, 0)
        setMinAnswer(min)
        setMinAnswerInput(formatNonNegativeInt(min, 0))
        if (notify) notifyMinMaxChange(min, maxAnswer)
        return min
    }

    const commitMaxAnswer = (raw, notify = true) => {
        const max = Math.max(1, parseNonNegativeInt(raw, 1))
        setMaxAnswer(max)
        setMaxAnswerInput(formatNonNegativeInt(max, 1))
        if (notify) notifyMinMaxChange(minAnswer, max)
        return max
    }

    const handleMinAnswerChange = value => {
        if (value === '' || value == null) {
            setMinAnswerInput('')
            return
        }
        const digits = String(value).replace(/\D/g, '')
        if (digits === '') {
            setMinAnswerInput('')
            return
        }
        commitMinAnswer(digits)
    }

    const handleMinAnswerBlur = () => {
        if (minAnswerInput === '') {
            commitMinAnswer(0)
        } else {
            commitMinAnswer(minAnswerInput)
        }
    }

    const handleMaxAnswerChange = value => {
        if (value === '' || value == null) {
            setMaxAnswerInput('')
            return
        }
        const digits = String(value).replace(/\D/g, '')
        if (digits === '') {
            setMaxAnswerInput('')
            return
        }
        commitMaxAnswer(digits)
    }

    const handleMaxAnswerBlur = () => {
        if (maxAnswerInput === '') {
            commitMaxAnswer(1)
        } else {
            commitMaxAnswer(maxAnswerInput)
        }
    }

    // Log templateId when modal is opened
    useEffect(() => {
        if (open) {
            console.log('CreateResponseModal opened, templateId:', templateId)
            setIsOtherOptionAvailable(
                typeof isOtherOptionAvailableProp === 'boolean'
                    ? isOtherOptionAvailableProp
                    : data?.IsOtherOptionAvailable || false,
            )
            const isNewGroup = !(data?.Items?.length)
            let min = initialMinAnswer
            if (data?.MinAnswer != null) {
                min = Number(data.MinAnswer)
            } else if (isNewGroup) {
                min = 0
            }
            let max = initialMaxAnswer
            if (data?.MaxAnswer != null) {
                max = Number(data.MaxAnswer)
            } else if (isNewGroup) {
                max = 1
            }
            const minVal = Number.isNaN(min) ? 0 : min
            const maxVal = Number.isNaN(max) ? 1 : max
            setMinAnswer(minVal)
            setMaxAnswer(maxVal)
            setMinAnswerInput(formatNonNegativeInt(minVal, 0))
            setMaxAnswerInput(formatNonNegativeInt(maxVal, 1))
        }
    }, [open, templateId, data, isOtherOptionAvailableProp, initialMinAnswer, initialMaxAnswer])

    useEffect(() => {
        if (open && respType === LIST_BOX_RESPONSE_TYPE) {
            notifyMinMaxChange(minAnswer, maxAnswer)
        }
    }, [open, respType, minAnswer, maxAnswer])

    const onResponceTypeChnage = id => {
        setrespType(id)
        if (id === LIST_BOX_RESPONSE_TYPE && data?.MinAnswer == null) {
            commitMinAnswer(0)
        }
    }
    useEffect(() => {
        if (data?.Items?.length) {
            setrespType(data?.Items?.[0]?.MultiResponseType || null)
        } else {
            setrespType(multipleResponcesList?.[0]?.Id)
        }
    }, [data])

    useEffect(() => {
        if (files?.length) {
            setrespType(multipleResponcesList?.[1]?.Id)
        }
    }, [files])

    const setScoreFromQuestion = (responseId, isChecked, currentQuestionId) => {
        const updatedResponses = responces.map(resp => {
            if (resp.QuestionId === currentQuestionId) {
                if (resp.Id === responseId) {
                    resp.IsScoreOnThisResponse = isChecked
                    if (isChecked) {
                        // Store the score internally, without filling the UI field
                        resp.fetchedScore = questionScore // Save the score value internally
                    } else {
                        // Reset the fetched score when deselected
                        resp.fetchedScore = undefined
                    }
                } else if (isChecked) {
                    // Unselect other responses in the same question
                    resp.IsScoreOnThisResponse = false
                }
            }
            return resp
        })
        setResponces(updatedResponses)
    }

    const addResposeField = () => {
        setResponces([
            ...responces,
            {
                DisplayText: '',
                BackColorCode: '#808080',
                ForeColorCode: '#FFFFFF',
                Score: '',
                AllowManualScore: false,
                MaxManualScore: 0,
                Id: responces[responces.length - 1].Id + 1,
                DisplayOrder: responces.length,
                GroupId: lastMultipleElement.GroupId + (editMode ? 0 : 1),
                DisplayType: responces[responces.length - 1].Id + 1,
                MultiResponseType: respType,
                tempId: editMode ? new Date().toISOString() : null,
                IsExcludedFromScoring: false,
            },
        ])
        setSuggestions([])
    }

    const submit = async () => {
        setLoading(true)
        let submitMin = minAnswer
        let submitMax = maxAnswer
        if (respType === LIST_BOX_RESPONSE_TYPE) {
            submitMin = commitMinAnswer(
                minAnswerInput === '' ? 0 : minAnswerInput,
                false,
            )
            submitMax = commitMaxAnswer(
                maxAnswerInput === '' ? 1 : maxAnswerInput,
                false,
            )
            notifyMinMaxChange(submitMin, submitMax)
        }
        try {
            if (responces.length) {
                console.log(
                    'CreateResponseModal - Using templateId in submit:',
                    templateId,
                )
                responces.forEach((val, index) => {
                    responces[index].DisplayOrder = index
                    responces[index].TemplateId = templateId
                    responces[index].GroupId = groupId
                    responces[index].MultiResponseType = respType
                    responces[index].Id = val.tempId ? 0 : val.Id
                    responces[index].AllowManualScore = !!responces[index].AllowManualScore
                    responces[index].MaxManualScore = Number(
                        responces[index].MaxManualScore,
                    )
                    if (Number.isNaN(responces[index].MaxManualScore)) {
                        responces[index].MaxManualScore = 0
                    }
                    // Ensure Hints is always an array
                    if (!responces[index].Hints) {
                        responces[index].Hints = []
                    }
                })
            }
            let result = {}
            if (editMode) {
                result = await TemplateService.updateTemplateMultiResponse(
                    responces,
                )
            } else {
                result = await TemplateService.saveTemplateMultiResponse(
                    responces,
                )
            }
            const closePayload = {
                Items: result.Items,
                IsOtherOptionAvailable: isOtherOptionAvailable,
            }
            if (respType === LIST_BOX_RESPONSE_TYPE) {
                closePayload.MinAnswer = submitMin
                closePayload.MaxAnswer = submitMax
            }
            onClose(closePayload)
            setrespType(null)
        } catch (error) {
            console.error('Submission error:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (open) {
            console.log('CreateResponseModal data:', data)
            const items = data.Items || data.item?.Items
            if (items && items.length) {
                // If we have templateId and groupId, fetch the latest data including hints
                if (templateId && items[0].GroupId) {
                    // Extract numeric Id from templateId if it's a UUID
                    const numericTemplateId = templateNumericId

                    console.log(
                        'Using templateId for API call:',
                        numericTemplateId,
                    )

                    TemplateService.getMultiResponsesByTemplateAndGroup(
                        numericTemplateId,
                        items[0].GroupId,
                    )
                        .then(response => {
                            if (response && response.length > 0) {
                                setResponces(
                                    response.map(item => ({
                                        ...item,
                                        OriginalScore: item.Score,
                                        AllowManualScore: !!item.AllowManualScore,
                                        MaxManualScore: Number(
                                            item.MaxManualScore,
                                        ) || 0,
                                    })),
                                )
                            } else {
                                setResponces(
                                    items.map(item => ({
                                        ...item,
                                        OriginalScore: item.Score,
                                        AllowManualScore: !!item.AllowManualScore,
                                        MaxManualScore: Number(
                                            item.MaxManualScore,
                                        ) || 0,
                                    })),
                                )
                            }
                        })
                        .catch(error => {
                            console.error(
                                'Error fetching multi-responses:',
                                error,
                            )
                            setResponces(
                                items.map(item => ({
                                    ...item,
                                    OriginalScore: item.Score,
                                    AllowManualScore: !!item.AllowManualScore,
                                    MaxManualScore: Number(
                                        item.MaxManualScore,
                                    ) || 0,
                                })),
                            )
                        })
                } else {
                    setResponces(
                        items.map(item => ({
                            ...item,
                            OriginalScore: item.Score,
                            AllowManualScore: !!item.AllowManualScore,
                            MaxManualScore: Number(item.MaxManualScore) || 0,
                        })),
                    )
                }
            } else {
                setResponces([
                    {
                        DisplayText: '',
                        BackColorCode: '#808080',
                        Score: '',
                        AllowManualScore: false,
                        MaxManualScore: 0,
                        OriginalScore: '',
                        Id: 0,
                        IsExcludedFromScoring: false,
                    },
                ])
            }
        }
    }, [open, data, templateId])

    const onDragEnd = result => {
        // dropped outside the list
        if (!result.destination) {
            return
        }
        // setHasUpdate(true);
        const items = CommonUtility.reorderList(
            responces,
            result.source.index,
            result.destination.index,
        )
        const orderList = items.map((val, i) => {
            return {
                ...val,
                DisplayOrder: i,
                DisplayType: i,
            }
        })
        setResponces(orderList)
    }

    const remove = Id => {
        const removed = responces.filter(val => val.Id !== Id)
        setResponces(removed)
        setSuggestions([])
    }

    const onchange = (name, data, Id) => {
        const newResponces = [...responces]
        const index = newResponces.findIndex(d => d.Id === Id)
        if (index > -1) {
            newResponces[index][name] = data
        }
        setResponces(newResponces)
        setSuggestions([])
    }

    useEffect(() => {
        if (data?.GroupId) {
            setResponces(data.Items)
            setLastMultipleElement(data.Items[data.Items.length - 1])
        } else {
            setGroupId(Math.random().toString())
        }
        setFiles([])
    }, [open, data])

    const onFileUpload = async file => {
        setFiles(file)
        try {
            console.log('Uploading file:', file[0])

            const uploadResponse = await InspectionService.uploadMedia({
                media: file[0].file,
                callfrom: 1,
                type: 'xlsx',
                isExcel: true,
            })
            console.log('Raw Upload Response:', uploadResponse)

            const processExcelData = data => {
                if (!Array.isArray(data)) return []
                return data
                    .map(item => {
                        if (item && typeof item.Value === 'string') {
                            return item.Value
                        }
                        console.log('Unexpected item structure:', item)
                        return null
                    })
                    .filter(Boolean)
            }

            if (!uploadResponse) {
                const reader = new FileReader()
                reader.onload = async e => {
                    try {
                        const data = new Uint8Array(e.target.result)
                        const workbook = read(data, { type: 'array' })
                        const firstSheet =
                            workbook.Sheets[workbook.SheetNames[0]]
                        const jsonData = utils.sheet_to_json(firstSheet, {
                            header: 1,
                            raw: true,
                        })

                        console.log('Excel Data:', jsonData)

                        // Improved: Show as in Excel (time, text, or number)
                        const columnData = jsonData
                            .slice(1)
                            .map((row, idx) => {
                                const cellRef = `A${idx + 2}` // +2 because slice(1) skips header, and Excel is 1-indexed
                                const cell = firstSheet[cellRef]
                                if (cell && cell.w) {
                                    return cell.w
                                }
                                // fallback to raw value as string
                                const v = row[0]
                                return v ? v.toString() : ''
                            })
                            .filter(value => value && value.trim() !== '')

                        console.log('Processed Column Data:', columnData)

                        if (columnData.length > 0) {
                            setExcelData(columnData)
                            setSuggestions([])
                            // Create a response for each value in the columnData
                            const responsesFromExcel = columnData.map(
                                (value, idx) => ({
                                    DisplayText: value,
                                    BackColorCode: '#808080',
                                    ForeColorCode: '#FFFFFF',
                                    Score: '',
                                    Id: idx,
                                    DisplayOrder: idx,
                                    GroupId: Math.random().toString(),
                                    DisplayType: idx,
                                    MultiResponseType: respType,
                                    IsExcludedFromScoring: false,
                                }),
                            )
                            setLastMultipleElement(
                                responsesFromExcel[
                                    responsesFromExcel.length - 1
                                ],
                            )
                            setResponces(responsesFromExcel)
                        } else {
                            setFileError(
                                'No valid data found in the Excel file',
                            )
                        }
                    } catch (error) {
                        console.error('Error parsing Excel file:', error)
                        setFileError('Error parsing Excel file')
                    }
                }
                reader.onerror = () => {
                    console.error('Error reading file')
                    setFileError('Error reading file')
                }
                reader.readAsArrayBuffer(file[0].file)
            } else {
                const columnData = processExcelData(uploadResponse)
                console.log('Processed Column Data:', columnData)

                if (columnData.length > 0) {
                    setExcelData(columnData)
                    setSuggestions([])
                    // Create a response for each value in the columnData
                    const responsesFromExcel = columnData.map((value, idx) => ({
                        DisplayText: value,
                        BackColorCode: '#808080',
                        ForeColorCode: '#FFFFFF',
                        Score: '',
                        Id: idx,
                        DisplayOrder: idx,
                        GroupId: Math.random().toString(),
                        DisplayType: idx,
                        MultiResponseType: respType,
                        IsExcludedFromScoring: false,
                    }))
                    setLastMultipleElement(
                        responsesFromExcel[responsesFromExcel.length - 1],
                    )
                    setResponces(responsesFromExcel)
                }
            }
        } catch (error) {
            console.error('File upload error:', error)
            setFileError('Error processing the file')
        }
    }

    const handleDisplayTextChange = (value, Id) => {
        console.log('Input Value:', value)
        console.log('Available Excel Data:', excelData)
        setActiveField(Id)

        if (value && excelData.length > 0) {
            const searchValue = value.toLowerCase().trim()
            const filtered = excelData.filter(item => {
                const itemLower = item.toString().toLowerCase()
                return itemLower.includes(searchValue)
            })
            console.log('Filtered Suggestions:', filtered)
            setSuggestions(filtered)
        } else {
            setSuggestions([])
        }
        onchange('DisplayText', value, Id)
    }

    const handleSuggestionClick = (suggestion, Id) => {
        onchange('DisplayText', suggestion, Id)
        setActiveField(null)
        setSuggestions([])
    }

    useEffect(() => {
        if (!open) {
            setSuggestions([])
        }
    }, [open])

    const openHintModal = index => {
        setCurrentHintIndex(index)
        const resp = responces[index]
        setHintHeading(resp?.Hints?.[0]?.Heading || '')
        setHintText(resp?.Hints?.[0]?.HintText || '')
        setHintFiles(
            resp?.Hints?.[0]?.MediaPath
                ? [
                      {
                          url: resp.Hints[0].MediaPath,
                          id: resp.Id || Date.now(),
                          name: 'Media',
                          local: false,
                          type: 'image',
                      },
                  ]
                : [],
        )
        setHintModalOpen(true)
    }
    const closeHintModal = () => {
        setHintModalOpen(false)
        setCurrentHintIndex(null)
        setHintHeading('')
        setHintText('')
        setHintFiles([])
    }
    const saveHint = () => {
        if (!hintHeading.trim()) {
            setHintHeadingError('Heading is required')
            return
        }
        setHintHeadingError('')
        if (currentHintIndex !== null) {
            const updated = [...responces]
            updated[currentHintIndex].Hints = [
                {
                    Heading: hintHeading,
                    HintText: hintText,
                    MediaPath: hintFiles[0]?.url || '',
                },
            ]
            setResponces(updated)
        }
        closeHintModal()
    }
    const onHintFileUpload = async uploadedFiles => {
        setHintUploadLoading(true)
        try {
            const uploadPromises = uploadedFiles.map(async file => {
                if (file.local) {
                    const fileToUpload = file.file
                    const uploadResponse = await InspectionService.uploadMedia({
                        media: fileToUpload,
                        type: fileToUpload.type,
                    })
                    const mediaPayload = [
                        {
                            MediaId: 0,
                            FAQId: '',
                            Order: 1,
                            FolderName: '1',
                            Comment: uploadResponse?.Comment || '',
                            Caption: uploadResponse?.Caption || '',
                            FileType:
                                uploadResponse?.FileType ||
                                fileToUpload.type ||
                                '',
                            SignedURL:
                                uploadResponse?.ImagePathUrl ||
                                uploadResponse?.FileURL ||
                                uploadResponse?.SignedURL,
                            FileURL:
                                uploadResponse?.ImagePathUrl ||
                                uploadResponse?.FileURL ||
                                uploadResponse?.SignedURL,
                            FileKey:
                                uploadResponse?.CloudImageId ||
                                uploadResponse?.FileKey,
                        },
                    ]
                    const savedMedia = await InspectionService.saveMedia(
                        mediaPayload,
                    )
                    const s3Url =
                        savedMedia[0].FileURL ||
                        savedMedia[0].SignedURL ||
                        uploadResponse.FileURL ||
                        uploadResponse.SignedURL
                    const savedFile = {
                        ...savedMedia[0],
                        id: savedMedia[0].MediaId,
                        name:
                            savedMedia[0].FileKey ||
                            uploadResponse.FileKey ||
                            fileToUpload.name ||
                            'File',
                        url: s3Url,
                        local: false,
                        type: fileToUpload.type,
                    }
                    setHintFiles([savedFile])
                    return savedFile
                }
                return file
            })
            await Promise.all(uploadPromises)
        } catch (error) {
            console.error('File upload error:', error)
        } finally {
            setHintUploadLoading(false)
        }
    }

    return (
        <Modal open={open} onClose={() => onClose()} className="model">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>
                        {editMode
                            ? 'Edit Multiple Responses'
                            : 'Create Multiple Responses'}
                    </TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <ResponseContainer>
                <Form noValidate>
                    <FlexRowBetween className="mt-0 mb-4">
                        <div className="col-4">
                            <ResponseTypeCheckBoxes
                                value={respType}
                                list={multipleResponcesList}
                                onChange={onResponceTypeChnage}
                            />
                        </div>
                        <div className="text-align-end">
                            <FileUploadPicker
                                maxFiles={1}
                                maxFileSize={500}
                                accept={AcceptFileType.xlsx}
                                addedNewFiles={onFileUpload}
                                files={files}
                                error={fileError}
                                tagPreview
                                showTakePicture={false}
                                uploadButtonText="Upload File"
                            />
                        </div>
                    </FlexRowBetween>
                    <Form.Field>
                        <Checkbox
                            label="Allow Other Response"
                            checked={isOtherOptionAvailable}
                            onChange={() => setIsOtherOptionAvailable(v => !v)}
                        />
                    </Form.Field>
                    {isListBox && (
                        <Form.Group className="align-items-center mt-2">
                            <Form.Field width={8}>
                                <label>Min Answer allowed</label>
                                <Form.Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={minAnswerInput}
                                    onChange={(_, { value }) =>
                                        handleMinAnswerChange(value)
                                    }
                                    onBlur={handleMinAnswerBlur}
                                />
                            </Form.Field>
                            <Form.Field width={8}>
                                <label>Max Answer allowed</label>
                                <Form.Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={maxAnswerInput}
                                    onChange={(_, { value }) =>
                                        handleMaxAnswerChange(value)
                                    }
                                    onBlur={handleMaxAnswerBlur}
                                />
                            </Form.Field>
                        </Form.Group>
                    )}
                    <List
                        list={responces}
                        remove={remove}
                        control={control}
                        errors={errors}
                        onchange={onchange}
                        onDragEnd={onDragEnd}
                        setScoreFromQuestion={setScoreFromQuestion}
                        handleDisplayTextChange={handleDisplayTextChange}
                        suggestions={suggestions}
                        handleSuggestionClick={handleSuggestionClick}
                        activeField={activeField}
                        onOpenHintModal={openHintModal}
                    />
                    <AddResponseButton onClick={addResposeField}>
                        <Plus size={24} />
                        <span className="mx-4">Create Response</span>
                    </AddResponseButton>
                    <SubmitCancelButtons
                        className="mt-4"
                        onSubmit={handleSubmit(submit)}
                        loading={loading}
                        onCancel={() => onClose()}
                    />
                </Form>
            </ResponseContainer>
            <ResponseHintModal
                open={hintModalOpen}
                onClose={closeHintModal}
                hintHeading={hintHeading}
                onHintHeadingChange={value => {
                    setHintHeading(value)
                    if (hintHeadingError) setHintHeadingError('')
                }}
                hintText={hintText}
                onHintTextChange={setHintText}
                hintHeadingError={hintHeadingError}
                hintFiles={hintFiles}
                onHintFileUpload={onHintFileUpload}
                onRemoveHintFile={() => setHintFiles([])}
                hintUploadLoading={hintUploadLoading}
                onSave={saveHint}
            />
        </Modal>
    )
}

export {
    AddResponseButton,
    ResponseContainer,
    List as MultiResponseList,
}

export default CreateResponseModal
